import React, { useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Checkbox,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { Field, useFormikContext } from "formik";
import { useDropdown } from "src/hooks/useDropdown.hook";

const PartnerInfoStep = () => {
  const { setFieldValue } = useFormikContext();
  const { data: ocpiRoles } = useDropdown("OcpiRole");
  const { data: ocpiVersions } = useDropdown("OcpiVersion");

  // Render function for OCPI Version select to display selected values
  const renderValue = useCallback(
    (selected) => {
      if (!selected) return "";
      return selected
        .map((id) => {
          const version = ocpiVersions.find((v) => +v.id === +id);
          return version ? version.value : id;
        })
        .join(", ");
    },
    [ocpiVersions]
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Basic Information
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Field name="partnerName">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Partner Name"
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
              />
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={3}>
          <Field name="partyId">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Party ID"
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={
                  (meta.touched && Boolean(meta.error)) ? meta.error : (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" color="textSecondary">
                        3-character unique identifier (e.g. ABC).
                      </Typography>
                    </Box>
                  )
                }
                margin="normal"
              />
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={3}>
          <Field name="countryCode">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Country Code"
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
              />
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Field name="ocpiRole">
            {({ field, meta }) => (
              <FormControl
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                margin="normal"
              >
                <InputLabel id="ocpi-role-label">OCPI Role</InputLabel>
                <Select
                  {...field}
                  labelId="ocpi-role-label"
                  label="OCPI Role"
                  value={field.value || ""}
                  onChange={(e) => setFieldValue("ocpiRole", e.target.value)}
                >
                  {ocpiRoles &&
                    ocpiRoles.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.value}
                      </MenuItem>
                    ))}
                </Select>
                {meta.touched && meta.error && (
                  <FormHelperText>{meta.error}</FormHelperText>
                )}
              </FormControl>
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Field name="ocpiVersion">
            {({ field, meta }) => (
              <FormControl
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                margin="normal"
              >
                <InputLabel id="ocpi-version-label">OCPI Version</InputLabel>
                <Select
                  labelId="ocpi-version-label"
                  label="OCPI Version"
                  multiple
                  value={field.value || []}
                  onChange={(e) => setFieldValue("ocpiVersion", e.target.value)}
                  renderValue={renderValue}
                >
                  {ocpiVersions &&
                    ocpiVersions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Checkbox
                            checked={field.value?.includes(opt.id) || false}
                            style={{ marginRight: 8 }}
                            readOnly // optional: use disabled if you want it to be uneditable
                          />
                          {opt.value}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
                {meta.touched && meta.error && (
                  <FormHelperText>{meta.error}</FormHelperText>
                )}
              </FormControl>
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Field name="businessWebsite">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Business Website"
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
              />
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={6}>
          <Field name="contactEmail">
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Contact Email"
                fullWidth
                size="small"
                error={meta.touched && Boolean(meta.error)}
                helperText={meta.touched && meta.error}
                margin="normal"
              />
            )}
          </Field>
        </Grid>
      </Grid>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, mt: 5 }}>
        Business Documents
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Field name="businessRegistration">
            {({ field, meta }) => (
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ py: 2 }}
                color={meta.touched && meta.error ? "error" : "primary"}
              >
                {field.value
                  ? "Business Registration Uploaded"
                  : "Upload Business Registration"}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  hidden
                  onChange={(e) =>
                    setFieldValue(
                      "businessRegistration",
                      e.currentTarget.files[0]
                    )
                  }
                />
              </Button>
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Field name="insuranceDocuments">
            {({ field, meta }) => (
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ py: 2 }}
                color={meta.touched && meta.error ? "error" : "primary"}
              >
                {field.value
                  ? "Insurance Documents Uploaded"
                  : "Upload Insurance Documents"}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  hidden
                  onChange={(e) =>
                    setFieldValue(
                      "insuranceDocuments",
                      e.currentTarget.files[0]
                    )
                  }
                />
              </Button>
            )}
          </Field>
        </Grid>
        <Grid item xs={12} md={4}>
          <Field name="complianceCertificates">
            {({ field, meta }) => (
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ py: 2 }}
                color={meta.touched && meta.error ? "error" : "primary"}
              >
                {field.value
                  ? "Compliance Certificates Uploaded"
                  : "Upload Compliance Certificates"}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  hidden
                  onChange={(e) =>
                    setFieldValue(
                      "complianceCertificates",
                      e.currentTarget.files[0]
                    )
                  }
                />
              </Button>
            )}
          </Field>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerInfoStep;
